import React, { useEffect, useState } from "react";
import { Star, Users, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

function fmtDate(iso) {
  if (!iso) return "\u2014";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AdminBetaFeedback() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [trackId, setTrackId] = useState("");

  async function refresh() {
    setLoading(true);
    try {
      const params = {};
      if (reviewerEmail.trim()) params.reviewer_email = reviewerEmail.trim();
      if (trackId.trim()) params.track_id = trackId.trim();
      const r = await api.get("/admin/beta/feedback", { params });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(errMsg(e, "Could not load beta feedback."));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="space-y-6" data-testid="admin-beta-feedback-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Kandake Operations · Industry Beta</div>
          <h1 className="font-display text-3xl tracking-tight flex items-center gap-3">
            <Users className="w-7 h-7" style={{ color: "hsl(347 60% 22%)" }} />
            Industry Beta Feedback
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            All feedback submitted by Industry Beta Reviewers. Reviewers see only their own history; this view
            is for authorised internal staff.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} data-testid="admin-beta-refresh">
          <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-end" data-testid="admin-beta-filters">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[11px] uppercase tracking-wide">Reviewer email</Label>
            <Input value={reviewerEmail} onChange={(e) => setReviewerEmail(e.target.value)} data-testid="admin-beta-filter-email" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[11px] uppercase tracking-wide">Track ID</Label>
            <Input value={trackId} onChange={(e) => setTrackId(e.target.value)} data-testid="admin-beta-filter-track" />
          </div>
          <Button size="sm" onClick={refresh} data-testid="admin-beta-apply">Apply</Button>
          <Button size="sm" variant="ghost" onClick={() => { setReviewerEmail(""); setTrackId(""); setTimeout(refresh, 0); }} data-testid="admin-beta-clear">Clear</Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-sm text-muted-foreground" data-testid="admin-beta-loading">Loading\u2026</div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-14 text-center text-sm text-muted-foreground" data-testid="admin-beta-empty">No feedback submitted yet.</CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="admin-beta-list">
          {rows.map((r) => (
            <Card key={r.id} data-testid={`admin-beta-row-${r.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                  <span>{r.track_title_snapshot || "General feedback"}</span>
                  {r.track_artist_snapshot && (
                    <span className="text-[12px] font-normal text-muted-foreground">\u00b7 {r.track_artist_snapshot}</span>
                  )}
                  {r.rating ? (
                    <span className="ml-auto inline-flex items-center gap-0.5">
                      {[1,2,3,4,5].map((n) => (
                        <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-amber-400 stroke-amber-500" : "stroke-slate-400"}`} />
                      ))}
                    </span>
                  ) : null}
                </CardTitle>
                <CardDescription className="text-[12px]">
                  <Badge variant="secondary" className="mr-2">{r.reviewer_email}</Badge>
                  {fmtDate(r.created_at)}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-[13px] grid sm:grid-cols-2 gap-3">
                {[
                  ["Search usefulness",        r.search_usefulness],
                  ["Metadata usefulness",      r.metadata_usefulness],
                  ["Clearance status clarity", r.clearance_status_clarity],
                  ["Missing information",      r.missing_information],
                  ["Ease of use",              r.ease_of_use],
                  ["Real workflow value",      r.helpful_for_real_workflow],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
                    <div className="whitespace-pre-wrap">{value || <span className="text-muted-foreground italic">\u2014</span>}</div>
                  </div>
                ))}
                {r.open_comments && (
                  <div className="sm:col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Open comments</div>
                    <div className="whitespace-pre-wrap">{r.open_comments}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
